import React, {
  Fragment,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import uniqby from "lodash.uniqby";
import { loadStripe } from "@stripe/stripe-js";
import { WindowLocation } from "@reach/router";

import { SelectedItem } from "../../lib/types";
import { GatsbyImage } from "gatsby-plugin-image";
import { useGlobalDispatch, useGlobalState } from "../../GlobalContext";
import { graphql, useStaticQuery } from "gatsby";
import { getTitleColor } from "../../lib/helpers";

const PlusIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
      />
    </svg>
  );
};

const MinusIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 12H4"
      />
    </svg>
  );
};

let stripePromise: any;
const getStripe = () => {
  const stripePublishableKey = process.env.GATSBY_STRIPE_PUBLISHABLE_KEY;

  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise;
};

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  location: WindowLocation<unknown>;
}
export const CartModal: FunctionComponent<Props> = ({
  open,
  setOpen,
  location,
}) => {
  const dispatch = useGlobalDispatch();
  const globalState = useGlobalState();

  const [selectedItems, setSelectedItems] = useState<SelectedItem[] | null>(
    null
  );
  const [priceTotal, setPriceTotal] = useState(0);
  const cancelButtonRef = useRef();

  const stripeData = useStaticQuery(graphql`
    query StripeProducts {
      allStripePrice {
        edges {
          node {
            id
            product {
              id
              description
              active
              name
              metadata {
                contentful_id
              }
            }
            active
            billing_scheme
            type
            unit_amount
          }
        }
        totalCount
      }
    }
  `);

  useEffect(() => {
    const initialSetUp = async () => {
      if (typeof window !== "undefined") {
        const selectedItems = JSON.parse(
          sessionStorage.getItem("PSYCHO_BETTY_SELECTIONS")
        );

        setSelectedItems(selectedItems);

        return selectedItems;
      }
    };

    const setTotalPrice = async (selectedItems: SelectedItem[]) => {
      if (selectedItems === null) {
        setPriceTotal(0);
      } else setPriceTotal(getPriceTotal(selectedItems));
    };

    const procedure = async () => {
      const selectedItems = await initialSetUp();
      await setTotalPrice(selectedItems);
    };

    if (typeof window !== "undefined") {
      procedure();
    }
  }, [open, globalState?.quantity]);

  const gatherLineItems = () => {
    const stripeProducts = stripeData?.allStripePrice?.edges;
    const uniqueItems = uniqby(selectedItems, "contentful_id");

    return uniqueItems.map((item) => {
      const quantity = getQuantity(item.contentful_id);
      const product = stripeProducts.find(
        ({ node }) => node.product.metadata.contentful_id === item.contentful_id
      );

      return { quantity, price: product.node.id };
    });
  };

  const handleSubmit = async () => {
    setOpen(false);

    const stripe = await getStripe();
    stripe.redirectToCheckout({
      mode: "payment",
      shippingAddressCollection: { allowedCountries: ["US"] },
      lineItems: gatherLineItems(),
      successUrl: `${window.location.origin}/thanks`,
      cancelUrl: `${window.location.origin}/${location.pathname}`,
    });
  };

  const getPriceTotal = (items: SelectedItem[]): number => {
    return items.reduce((a, c) => {
      return c.price + a;
    }, 0);
  };

  const getQuantity = (id: string) => {
    return selectedItems.filter((item) => item.contentful_id === id).length;
  };

  const deleteItem = (deletedItem: SelectedItem) => {
    if (typeof window !== "undefined") {
      const itemsWithDeletion = selectedItems.filter(
        (item) => item.contentful_id !== deletedItem.contentful_id
      );

      sessionStorage.setItem(
        "PSYCHO_BETTY_SELECTIONS",
        JSON.stringify(itemsWithDeletion)
      );

      dispatch({ type: "updateCartIcon", quantity: itemsWithDeletion.length });

      setSelectedItems(
        JSON.parse(sessionStorage.getItem("PSYCHO_BETTY_SELECTIONS"))
      );
    }
  };

  const decrementItem = (decrementedItem: SelectedItem) => {
    if (typeof window !== "undefined") {
      const itemsWithDeletion = selectedItems.filter(
        (item) => item.contentful_id !== decrementedItem.contentful_id
      );
      const decrementedItems = selectedItems.filter(
        (item) => item.contentful_id === decrementedItem.contentful_id
      );

      decrementedItems.pop();

      const itemsWithDecrement = [...itemsWithDeletion, ...decrementedItems];

      sessionStorage.setItem(
        "PSYCHO_BETTY_SELECTIONS",
        JSON.stringify(itemsWithDecrement)
      );

      dispatch({
        type: "updateCartIcon",
        quantity: JSON.parse(sessionStorage.getItem("PSYCHO_BETTY_SELECTIONS"))
          .length,
      });
    }
  };

  const incrementItem = (incrementededItem: SelectedItem) => {
    if (typeof window !== "undefined") {
      const itemToAdd = selectedItems.find(
        (item) => item.contentful_id === incrementededItem.contentful_id
      );
      const withAddition = [...selectedItems, itemToAdd];

      sessionStorage.setItem(
        "PSYCHO_BETTY_SELECTIONS",
        JSON.stringify(withAddition)
      );

      dispatch({
        type: "updateCartIcon",
        quantity: JSON.parse(sessionStorage.getItem("PSYCHO_BETTY_SELECTIONS"))
          .length,
      });
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto lg:max-w-full font-inter"
        initialFocus={cancelButtonRef}
        open={open}
        onClose={setOpen}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            {open ? (
              <div className="inline-block align-bottom bg-white rounded-md px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:align-middle sm:max-w-3xl w-full sm:p-6">
                <div>
                  <div className="relative">
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center mt-2 pb-0">
                      <span className="px-3 bg-white text-2xl font-normal text-black">
                        Edit Cart
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <div className="mt-0">
                      <div className="flow-root mt-6">
                        <ul className="-my-5 divide-y divide-gray-300">
                          {uniqby(selectedItems, "contentful_id")
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map((item, i) => (
                              <li
                                key={item.contentful_id}
                                className={` ${i !== 0 ? "py-4" : "pb-4"}`}
                              >
                                <div className="flex items-center mt-1">
                                  <div className="flex-shrink-0 rounded-md h-full">
                                    <GatsbyImage
                                      className="h-20 w-20 sm:h-28 sm:w-28 rounded-md"
                                      image={item.productImage.gatsbyImageData}
                                      alt={item.title}
                                      imgStyle={{ borderRadius: "6px" }}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0 px-2 h-full">
                                    <div className="text-sm sm:text-2xl font-thin text-black truncate">
                                      {`$${item.price} x ${getQuantity(
                                        item.contentful_id
                                      )} = $${(
                                        item.price *
                                        getQuantity(item.contentful_id)
                                      ).toFixed(2)}`}
                                      <p
                                        className={`text-xl sm:text-4xl ${getTitleColor(
                                          item.title
                                        )} truncate font-amaticSC font-bold`}
                                      >
                                        {item.title}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex justify-items-end items-end flex-col h-full">
                                    <button
                                      onClick={() => deleteItem(item)}
                                      type="button"
                                      className="inline-flex items-center sm:mb-2"
                                    >
                                      <XIcon
                                        className="w-6 h-6 inline-flex justify-center items-center rounded-full text-base border border-gray-transparent shadow-sm p-1 mt-1 sm:mt-0 bg-white text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black sm:col-start-1 font-thin"
                                        aria-hidden="false"
                                      />{" "}
                                    </button>

                                    <div className="flex justify-items-end items-end sm:flex-row mt-3 flex-col">
                                      <button
                                        type="button"
                                        className="sm:mr-2 w-full inline-flex justify-center items-center rounded-md text-base sm:text-xl border border-gray-transparent shadow-sm p-1 mt-1 sm:mt-0 bg-gray-200 text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black sm:col-start-1 font-thin"
                                        onClick={() => incrementItem(item)}
                                      >
                                        <PlusIcon />
                                      </button>
                                      <button
                                        type="button"
                                        className="w-full inline-flex justify-center items-center rounded-md text-base sm:text-xl border border-gray-transparent shadow-sm p-1 mt-1 sm:mt-0 bg-gray-200 text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black sm:col-start-1 font-thin"
                                        onClick={() => decrementItem(item)}
                                      >
                                        <MinusIcon />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          {selectedItems === null ||
                          selectedItems.length === 0 ? (
                            <li key="no-items" className="py-4">
                              <div className="text-black rounded-md md:text-3xl">
                                No Items Selected
                              </div>
                            </li>
                          ) : null}
                          <li key="totals" className="py-4">
                            <div className="text-black rounded-md text-3xl font-thin">
                              Total ${priceTotal.toFixed(2)}
                            </div>
                          </li>
                        </ul>
                      </div>{" "}
                    </div>
                  </div>
                </div>
                <div
                  className={`mt-5 ${
                    selectedItems === null || selectedItems.length === 0
                      ? ""
                      : "sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                  }`}
                >
                  {selectedItems === null ||
                  selectedItems.length === 0 ? null : (
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-thin text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black sm:col-start-2 sm:text-2xl"
                      onClick={handleSubmit}
                    >
                      Checkout
                    </button>
                  )}
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-transparent text-base font-thin text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black sm:mt-0 sm:col-start-1 sm:text-2xl"
                    onClick={() => setOpen(false)}
                    ref={cancelButtonRef}
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            ) : (
              <div></div>
            )}
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
